import { type DiscoverChatbotCardViewModel } from "@next-platform/client-common-core";
import { DropDownMenu, type DropDownMenuItem, Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import React, { useCallback } from "react";

import DiscoverChatbotCardArtwork from "@/pages/Discover/components/DiscoverChatbotCardArtwork";
import { DISCOVER_CHATBOT_CARD_ART_ASPECT_RATIO } from "@/pages/Discover/components/discoverChatbotCardDimensions";
import DiscoverChatbotPersonalityRing, {
  DISCOVER_CHATBOT_CARD_PERSONALITY_GLOW_CLASS_NAME,
} from "@/pages/Discover/components/DiscoverChatbotPersonalityRing";
import formatDiscoverChatbotCardCreatorHandle from "@/pages/Discover/components/formatDiscoverChatbotCardCreatorHandle";
import type { DiscoverChatbotCardLabels } from "@/pages/Discover/hooks/useDiscoverChatbotCardLabels";

const ACTION_BUTTON_CLASS_NAME =
  "relative z-[1] flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full bg-[rgba(0,0,0,0.36)] disabled:cursor-not-allowed disabled:opacity-50";
const NAME_COLOR = "#FFFFFF";
const ART_ASPECT_RATIO_STYLE = { aspectRatio: DISCOVER_CHATBOT_CARD_ART_ASPECT_RATIO };

type DiscoverChatbotCardArtProps = {
  model: DiscoverChatbotCardViewModel;
  labels: DiscoverChatbotCardLabels;
  dropdownMenuItems: DropDownMenuItem[];
  isAuthenticated: boolean;
  isFavoritePending: boolean;
  testId: string;
  onFavoritePress: (characterId: string) => void;
};

const DiscoverChatbotCardArt = ({
  model,
  labels,
  dropdownMenuItems,
  isAuthenticated,
  isFavoritePending,
  testId,
  onFavoritePress,
}: DiscoverChatbotCardArtProps) => {
  const creatorHandle = formatDiscoverChatbotCardCreatorHandle(model.creatorUsername);

  const handleFavoritePress = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onFavoritePress(model.characterId);
    },
    [onFavoritePress, model.characterId],
  );

  return (
    <div className="relative w-full overflow-hidden" style={ART_ASPECT_RATIO_STYLE} data-testid={`${testId}-Art`}>
      <DiscoverChatbotCardArtwork
        name={model.name}
        avatarUrl={model.avatarUrl}
        isAvatarBlurred={model.isAvatarBlurred}
        data-testid={`${testId}-Image`}
      />

      {model.isRecommended ? (
        <div
          className="pointer-events-none absolute top-0 left-1/2 z-[4] max-w-[70%] -translate-x-1/2 truncate rounded-b-lg bg-purple-9 px-2 py-0.5"
          data-testid={`${testId}-RecommendedBadge`}
        >
          <span className="text-[9px] font-bold whitespace-nowrap text-white">{labels.recommended}</span>
        </div>
      ) : null}

      <div className="absolute inset-x-0 top-0 z-[3] flex items-center p-1.5" data-testid={`${testId}-Actions`}>
        {isAuthenticated ? (
          <button
            type="button"
            data-testid={`${testId}-FavoriteButton`}
            aria-label={model.isFavorite ? labels.favorited : labels.favorite}
            className={ACTION_BUTTON_CLASS_NAME}
            disabled={isFavoritePending}
            onClick={handleFavoritePress}
          >
            <Icon
              icon={IconVariants.FAVORITE}
              size={IconSize.SMALL}
              className={model.isFavorite ? "fill-red-9 text-red-9" : "fill-transparent text-white"}
            />
          </button>
        ) : null}

        {dropdownMenuItems.length > 0 ? (
          <div className="ml-auto">
            <DropDownMenu listItems={dropdownMenuItems} isPortaled>
              <button
                type="button"
                className={
                  model.isDefinitionVisible
                    ? `${ACTION_BUTTON_CLASS_NAME} ${DISCOVER_CHATBOT_CARD_PERSONALITY_GLOW_CLASS_NAME}`
                    : ACTION_BUTTON_CLASS_NAME
                }
                aria-label={labels.more}
                data-testid={`${testId}-MoreButton`}
              >
                {model.isDefinitionVisible ? <DiscoverChatbotPersonalityRing data-testid={`${testId}-MoreButton-PersonalityRing`} /> : null}
                <Icon icon={IconVariants.MORE_VERTICAL} size={IconSize.SMALL} color={NAME_COLOR} />
              </button>
            </DropDownMenu>
          </div>
        ) : null}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-[rgba(22,22,24,0.9)] to-transparent" />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] px-2 pb-2">
        <p className="m-0 truncate text-sm leading-[18px] font-semibold text-white">{model.name}</p>
        {creatorHandle ? <p className="m-0 truncate text-[10px] leading-[14px] text-white/[.66]">{creatorHandle}</p> : null}
      </div>
    </div>
  );
};

export default React.memo(DiscoverChatbotCardArt);
